import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * EL UMBRAL — el silencio antes de leer.
 *
 * Faina lo aprobó el 26-ago («haz lo del silencio, y déjale claro que no durará
 * más de 4 minutos») y ese mismo día lo acotó: **vive SOLO en el mensaje del día
 * del home**, no dentro de cada canalización. Tiene sentido — el umbral es un
 * gesto de entrada, y se cruza una vez al llegar, no catorce veces mientras se
 * navega. Repetido en cada pieza dejaba de ser un rito y pasaba a ser un peaje.
 *
 * POR QUÉ EXISTE. Estas canalizaciones se RECIBIERON en silencio: alguien se
 * sentó, se aquietó y escuchó. Y la portada las ofrecía como cualquier web
 * ofrece un artículo. Toda tradición contemplativa pone un umbral antes de la
 * lectura sagrada: la lectio divina no empieza en el texto, empieza en el
 * silencio que lo precede.
 *
 * QUÉ NO ES. No bloquea nada: el visitante puede irse a la biblioteca, a las
 * voces o a cualquier pieza sin pasar por aquí. Es una invitación, no una
 * puerta.
 *
 * EL EMBLEMA VA DENTRO DEL CÍRCULO que respira. Al principio el círculo llevaba
 * un núcleo de luz y el emblema de la voz iba aparte, encima: dos círculos en la
 * misma franja, compitiendo. Metiendo uno dentro del otro hay una sola figura y
 * además respira el símbolo de quien habla ese día, que es lo que se quería.
 *
 * 🔴 Y DESDE EL 29-ago EL CÍRCULO ABRE, EN GRANDE (Faina eligió el formato «la
 * imagen manda» entre cuatro). El componente se parte en dos: la figura arriba
 * y la invitación abajo, con el título y el primer párrafo EN MEDIO —lo que
 * llegue como `children`—.
 *
 * Por qué así: en el formato anterior la foto aparecía DESPUÉS del párrafo, o
 * sea cuando el lector ya había decidido si seguía; y a 124 px el rayo de
 * Miguel era una mancha. Grande y arriba, se ve quién habla antes de leer nada,
 * y la figura que respira es la misma que se está mirando: el gesto y la imagen
 * dicen lo mismo en vez de repartirse la franja.
 *
 * LA RESPIRACIÓN son 4 segundos dentro y 4 fuera, tres veces, y se aquieta: no
 * queremos hipnotizar a nadie, solo cambiar el paso. Con `prefers-reduced-motion`
 * no se mueve —hay gente a la que el movimiento le da náusea de verdad— y el
 * umbral sigue diciendo lo mismo.
 */

const RESPIRACIONES = 3;
const CICLO = 8000;   // 4 s dentro + 4 s fuera

const Umbral = ({ minutos, rayo, emblema, a, grande = false, children }) => {
  const { t } = useTranslation();
  const [fase, setFase] = useState('dentro');
  const [quieto, setQuieto] = useState(false);
  const contador = useRef(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setQuieto(true);
      return undefined;
    }
    const t = setInterval(() => {
      setFase((f) => {
        if (f === 'fuera') {
          contador.current += 1;
          if (contador.current >= RESPIRACIONES) { setQuieto(true); clearInterval(t); }
        }
        return f === 'dentro' ? 'fuera' : 'dentro';
      });
    }, CICLO / 2);
    return () => clearInterval(t);
  }, []);

  return (
    <Box className="umbral" style={{ '--rayo': rayo }}>
      <Box className={`umbral-circulo${grande ? ' umbral-circulo-grande' : ''}`
                      + ` umbral-${quieto ? 'quieto' : fase}`}>
        {emblema || <span className="umbral-nucleo" aria-hidden="true" />}
      </Box>

      {/* Lo que va entre la figura y la invitación: quién habla, el título y su
          primer párrafo. Sin `children`, el umbral se comporta como antes. */}
      {children}

      {/* «Respira antes de empezar» salio el 20-sep (Faina: «elimínalo»). La
          figura que respira se queda; la instruccion, no. */}

      {/* La duración sale del dato de ESTA pieza, no de una promesa general:
          88 de las 94 se leen en 4 minutos o menos, pero seis son de cinco, y
          prometer «nunca más de 4» sería falso justo en esas seis. */}
      <Typography className="umbral-dura">{t('umbral.minutos', { n: minutos })}</Typography>

      <RouterLink to={a} className="umbral-boton">{t('umbral.estoy')}</RouterLink>
    </Box>
  );
};

export default Umbral;
